'use client';

import React, { useState, useEffect } from 'react';
import { Bookmark, BookmarkCheck, Loader2 } from 'lucide-react';
import { collectionsService, SavePropertyInput } from '@/lib/collections';
import { useAuth } from '@/contexts/AuthContext';
import SaveToCollectionModal from './SaveToCollectionModal';

interface SavePropertyButtonProps {
  property: SavePropertyInput;
  variant?: 'icon' | 'button';
  className?: string;
}

export default function SavePropertyButton({ property, variant = 'icon', className = '' }: SavePropertyButtonProps) {
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    collectionsService.getPropertyCollections(property.propertyId).then((ids) => {
      if (!cancelled) {
        setIsSaved(ids.length > 0);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [user, property.propertyId]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      window.location.href = '/giris';
      return;
    }
    setModalOpen(true);
  };

  if (variant === 'button') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all duration-150 ${
            isSaved
              ? 'bg-primary-light text-primary border-primary/30' :'border-border text-foreground hover:bg-primary-light hover:text-primary hover:border-primary/30'
          } ${className}`}
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : isSaved ? (
            <BookmarkCheck size={16} />
          ) : (
            <Bookmark size={16} />
          )}
          <span className="hidden sm:inline">{isSaved ? 'Kaydedildi' : 'Kaydet'}</span>
        </button>
        <SaveToCollectionModal
          isOpen={modalOpen}
          onClose={() => { setModalOpen(false); collectionsService.getPropertyCollections(property.propertyId).then((ids) => setIsSaved(ids.length > 0)); }}
          property={property}
        />
      </>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 shadow-md ${
          isSaved
            ? 'bg-primary text-white' :'bg-white text-foreground hover:bg-primary hover:text-white'
        } ${className}`}
        aria-label="Koleksiyona kaydet"
      >
        {loading ? (
          <Loader2 size={13} className="animate-spin" />
        ) : isSaved ? (
          <BookmarkCheck size={14} />
        ) : (
          <Bookmark size={14} />
        )}
      </button>
      <SaveToCollectionModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); collectionsService.getPropertyCollections(property.propertyId).then((ids) => setIsSaved(ids.length > 0)); }}
        property={property}
      />
    </>
  );
}
